import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAutenticacao } from '../context/autenticacao_contexto';
import logoMedicina from '../assets/logo-medicina.png';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Box,
    Container,
    Button,
    Avatar,
    Menu,
    MenuItem,
    Divider,
    useTheme
} from '@mui/material';
import {
    Logout,
    LocalHospital,
    AdminPanelSettings,
    Person,
    ExpandMore
} from '@mui/icons-material';
import { cores } from '../theme';

const LayoutPrincipal = ({ children }) => {
    const navegar = useNavigate();
    const { perfilUsuario, logout } = useAutenticacao();
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const theme = useTheme();

    const lidarComLogout = async () => {
        try {
            await logout();
            navegar('/login');
        } catch (erro) {
            console.error("Erro ao fazer logout:", erro);
        }
    };

    const abrirMenu = (evento) => {
        setMenuAnchor(evento.currentTarget);
    };

    const fecharMenu = () => {
        setMenuAnchor(null);
    };

    const irParaGerenciamento = () => {
        navegar('/gerenciar');
        fecharMenu();
    };


    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar
                component="nav"
                sx={{
                    background: cores.gradientes.primario,
                    boxShadow: cores.sombras.elevada,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 0
                }}
                position="fixed"
            >
                <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
                    <Box
                        component={RouterLink}
                        to="/"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexGrow: 1,
                            textDecoration: 'none',
                            color: 'inherit',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                                transform: 'scale(1.02)'
                            }
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 48,
                                height: 48,
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                marginRight: 1.5,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Box
                                component="img"
                                src={logoMedicina}
                                alt="Logo RealMed Medicina"
                                sx={{
                                    height: 34,
                                    filter: 'brightness(1.1)'
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h5"
                            component="div"
                            sx={{
                                fontWeight: 'bold',
                                background: 'linear-gradient(45deg, #ffffff, #e3f2fd)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            }}
                        >
                            RealMed
                        </Typography>
                    </Box>

                    {perfilUsuario && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    color: cores.fundo.papel,
                                    fontWeight: 500
                                }}
                            >
                                Olá, {perfilUsuario.nome_completo?.split(' ')[0] || 'Usuário'}
                            </Typography>

                            <IconButton
                                onClick={abrirMenu}
                                sx={{
                                    padding: 0.5,
                                    border: '2px solid rgba(255, 255, 255, 0.3)',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    '&:hover': {
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        borderColor: 'rgba(255, 255, 255, 0.5)'
                                    }
                                }}
                            >
                                <Avatar
                                    src={perfilUsuario.foto_perfil_url || undefined}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: cores.primaria.escura,
                                        color: cores.fundo.padrao,
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold',
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    {!perfilUsuario.foto_perfil_url &&
                                        (perfilUsuario.nome_completo?.charAt(0)?.toUpperCase() || <Person />)
                                    }
                                </Avatar>
                            </IconButton>

                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={fecharMenu}
                                PaperProps={{
                                    sx: {
                                        minWidth: 200,
                                        borderRadius: 2,
                                        boxShadow: cores.sombras.elevada,
                                        border: `1px solid ${cores.primaria.principal}10`,
                                        mt: 1
                                    }
                                }}
                            >
                                <Box sx={{ px: 2, py: 1.5 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {perfilUsuario.nome_completo || 'Usuário'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {perfilUsuario.email}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {perfilUsuario.is_admin ? 'Administrador' :
                                            perfilUsuario.is_teacher ? 'Professor' : 'Aluno'}
                                    </Typography>
                                </Box>
                                <Divider />

                                <MenuItem onClick={() => { navegar('/meu-perfil'); fecharMenu(); }}>
                                    <Person sx={{ mr: 2 }} />
                                    Meu Perfil
                                </MenuItem>

                                {perfilUsuario?.is_admin && (
                                    <MenuItem onClick={irParaGerenciamento}>
                                        <AdminPanelSettings sx={{ mr: 2 }} />
                                        Gerenciar Sistema
                                    </MenuItem>
                                )}

                                <MenuItem onClick={lidarComLogout} sx={{ color: cores.erro.principal }}>
                                    <Logout sx={{ mr: 2 }} />
                                    Sair
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            <Box
                component="main"
                sx={{
                    width: '100%',
                    minHeight: '100vh',
                    background: cores.gradientes.fundo,
                    pt: { xs: 8, sm: 9 },
                    pb: 3
                }}
            >
                <Container maxWidth="lg">
                    {children}
                </Container>
            </Box>
        </Box>
    );
};

export default LayoutPrincipal;